import { SITE_URL } from "@/components/Seo";

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem", position: i + 1, name: it.label,
    ...(it.to ? { item: SITE_URL + it.to } : {}),
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const COMPLAINT_CATEGORIES = [
  "Non-payment", "Delayed payment", "Incomplete payment", "Poor service", "Incomplete work",
  "Delayed work", "Product quality issue", "Wrong product supplied", "Warranty refusal",
  "Delayed refund", "Overcharging", "Misrepresentation", "Property damage",
  "Contract disagreement", "Rude or inappropriate communication", "False or disputed complaint", "Other issue",
];

export const CHECK_LIST = [
  "Confirm the person or business name.",
  "Check the service or product description.",
  "Clarify pricing and payment milestones.",
  "Request a written quotation or agreement.",
  "Confirm delivery or completion timelines.",
  "Understand warranty and refund terms.",
  "Keep invoices and payment records.",
  "Avoid sharing unnecessary personal information.",
  "Review available feedback and complaint responses.",
  "Use written communication for important commitments.",
];

export const FAQ_ITEMS = [
  { q: "Is a complaint automatically treated as proven?", a: "No. A complaint is an allegation or report that may be incomplete, disputed, withdrawn, or under moderation. The concerned party may be invited to respond or challenge it." },
  { q: "Can a vendor respond to a complaint?", a: "Yes, where the workflow and account status permit. Responses should remain factual and comply with community guidelines." },
  { q: "Does ComplaintAdvisor guarantee a refund?", a: "No. ComplaintAdvisor does not guarantee refunds, recovery, settlement, or legal outcomes." },
  { q: "Can I upload private documents?", a: "Upload only information necessary for the issue. Do not upload passwords, complete payment-card details, identity documents, or unnecessary sensitive information. Private evidence must not be publicly displayed." },
  { q: "Can a complaint be corrected?", a: "Users may request correction through the correction process. Requests are reviewed according to the Complaint Policy and applicable privacy requirements." },
  { q: "Can a complaint be removed?", a: "Removal is not automatic. Requests may be reviewed for privacy, policy violations, inaccuracies, legal requirements, duplication, or other valid grounds." },
  { q: "Are featured vendors guaranteed to be reliable?", a: "No. Featured placement is promotional and does not guarantee service quality or complaint-free performance." },
  { q: "Is ComplaintAdvisor a government authority?", a: "No. ComplaintAdvisor is an independent technology and information platform." },
  { q: "Can I find a lawyer through the platform?", a: "The directory may provide publicly supplied or consent-based professional information. Users should independently verify credentials and obtain advice directly from the lawyer." },
  { q: "Can I search by mobile number?", a: "Mobile numbers may be used as lookup identifiers where permitted. A mobile number is not legal identity proof, and search results may be incomplete or disputed." },
];

export const PRICING_PLANS = [
  { name: "Free", price: "₹0", period: "forever", features: ["Basic business profile", "Category listing", "Public enquiry option", "Basic profile information"] },
  { name: "Starter", price: "₹499", period: "per month", features: ["Enhanced business profile", "Additional service details", "Enquiry management", "Basic performance information"] },
  { name: "Growth", price: "₹1,499", period: "per month", popular: true, features: ["Enhanced profile visibility", "Additional media or service sections", "Enquiry management tools", "Promotional options"] },
  { name: "Premium", price: "₹3,999", period: "per month", features: ["Expanded promotional placement", "Advanced profile tools", "Priority support, if available", "Advertising options"] },
];

export const VERIFICATION_LABELS = [
  ["Contact information submitted", "The business has submitted contact details for review."],
  ["Business information submitted", "The business has submitted business details for review."],
  ["Document review pending", "Submitted documents are awaiting review."],
  ["Verification completed", "A limited verification check was completed for specific attributes."],
  ["Verification expired", "A previous verification is no longer current."],
  ["Verification unavailable", "No verification has been performed for this profile."],
  ["Demo profile", "An illustrative example profile — not a real business."],
];

export const GOV_TOPICS = [
  ["consumer-complaints", "Consumer Complaints", "Issues with goods or services, deficiency, unfair trade practice or defective products."],
  ["banking-complaints", "Banking Complaints", "Problems with bank accounts, cards, loans, charges, or digital transactions."],
  ["insurance-complaints", "Insurance Complaints", "Claim delays, rejections, mis-selling, or policy servicing issues."],
  ["cybercrime-reporting", "Cybercrime Reporting", "Online fraud, phishing, financial scams, or cyber harassment."],
  ["telecom-complaints", "Telecom Complaints", "Mobile, broadband, billing, network, or service quality issues."],
  ["railway-complaints", "Railway Complaints", "Train services, refunds, cleanliness, or booking-related issues."],
  ["airline-complaints", "Airline Complaints", "Flight delays, cancellations, refunds, or baggage issues."],
  ["electricity-utility-complaints", "Electricity & Utility Complaints", "Power supply, metering, billing, or connection issues."],
  ["rera-property-complaints", "RERA & Property Complaints", "Real-estate project delays, possession, or builder disputes."],
  ["food-safety-complaints", "Food Safety Complaints", "Food quality, hygiene, adulteration, or labelling concerns."],
  ["education-complaints", "Education Complaints", "Institution, fee, or admission-related grievances."],
  ["municipal-complaints", "Municipal Complaints", "Civic issues such as sanitation, water, roads, or public services."],
  ["labour-employment-complaints", "Labour & Employment Complaints", "Wages, workplace, or employment-condition grievances."],
  ["state-grievance-portals", "State Grievance Portals", "State-level public grievance redressal systems."],
];

export const GOV_TOPIC_GUIDE = {
  whenToUse: "Use an official government channel when an issue cannot be resolved directly with the business, involves a regulated service, or requires a formal grievance record.",
  prepare: ["A clear, factual summary of the issue", "Relevant dates and reference numbers", "Invoices, receipts, or transaction proof", "Prior communication with the business", "Your contact details"],
  steps: ["Try to resolve directly with the business and keep written records.", "Identify the correct official authority for your issue and location.", "Prepare your documents and a concise factual complaint.", "Submit the complaint through the official portal or helpline.", "Note your reference number and follow the escalation path if unresolved."],
};

export const LAWYER_PRACTICE_AREAS = [
  "Consumer disputes", "Contract disputes", "Recovery matters", "Property disputes", "Banking matters",
  "Insurance matters", "Cybercrime-related assistance", "Employment matters", "Commercial disputes", "Civil matters",
];

export const LAWYER_CITIES = ["Mumbai", "Delhi", "Bengaluru", "Pune", "Hyderabad", "Chennai", "Kolkata", "Ahmedabad", "Jaipur", "Lucknow"];

export const ABOUT_SUPPORTS = [
  "Structured complaint documentation", "Review and feedback discovery", "Customer and vendor communication",
  "Dispute-resolution workflows", "Vendor profile discovery", "Access to official complaint information", "Professional-help discovery",
];
export const ABOUT_PRINCIPLES = ["Neutrality", "Privacy", "Transparency", "Due process", "Factual communication", "Response opportunities", "Responsible moderation"];

export const FEATURED_DISCLOSURE = "Featured placement may be paid. Payment does not influence complaint decisions, review publication, moderation, or dispute outcomes.";
